import type { GetStaticPaths, GetStaticProps } from 'next'
import type { FC } from 'react'
import type { Author, Category, Post } from '@/types'

import { Seo } from '@/components/generals/seo'
import { SiteLayout } from '@/components/layout/site-layout'
import { GoldCta } from '@/components/products'

import { AuthorPhotoTilt, BlogGridCard } from '@/components/blog'

import { getLayoutData } from '@/lib/layout-data'
import { getAuthorBySlug, getAuthors, getPostsByAuthor } from '@/lib/collections'

interface AuthorPageProps {
    categories: Category[]
    author: Author
    posts: Post[]
}

const AuthorPage: FC<AuthorPageProps> = ({ categories, author, posts }) => {
    return (
        <SiteLayout categories={categories}>
            <Seo title={author.name} description={author.bioSummary} ogImage={author.picture} />

            <div className='section author-section'>
                <div className='container _6ems'>
                    <div className='author-border-container'>
                        <div className='author-holder'>
                            <div className='author-details-container'>
                                <div className='animation-on-load-01'>
                                    <h2 className='no-margins'>{author.name}</h2>
                                    <div className='author-role'>{author.role}</div>
                                </div>
                                <div className='animation-on-load-02'>
                                    <p className='grey-text'>{author.bioSummary}</p>
                                </div>
                            </div>
                            <div className='animation-on-load-03'>
                                <AuthorPhotoTilt image={author.picture} name={author.name} />
                            </div>
                        </div>
                        <div className='animation-on-load-03'>
                            <div className='author-links'>
                                <a href={author.facebook || '#'} className='author-link-holder w-inline-block'>
                                    <img src='/images/Author-Facebook-Icon.svg' loading='lazy' alt='' className='author-social-media-link' />
                                </a>
                                <a href={author.twitter || '#'} className='author-link-holder w-inline-block'>
                                    <img src='/images/Author-Twiter-Icon.svg' loading='lazy' alt='' className='author-social-media-link' />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='animation-on-load-04'>
                <div className='section grey-section author'>
                    <div className='container _6ems'>
                        <h3>More News</h3>
                        <div className='blog-grid-wrapper w-dyn-list'>
                            <div role='list' className='blog-grid w-dyn-items'>
                                {posts.map((post) => (
                                    <BlogGridCard key={post.slug} post={post} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <GoldCta />
        </SiteLayout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: getAuthors().map((author) => ({ params: { slug: author.slug } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<AuthorPageProps> = async ({ params }) => {
    const slug = params?.slug as string
    const author = getAuthorBySlug(slug)

    if (!author) {
        return { notFound: true }
    }

    const { categories } = getLayoutData()

    return {
        props: {
            categories,
            author,
            posts: getPostsByAuthor(slug)
        }
    }
}

export default AuthorPage
